<template>
  <page-template :is-loading="isLoading" :data-exists="backendServices !== null">
    <template #pageHeadline>
      {{ $t('backendService.listPage.pageHeadline') }}
    </template>
    <template #pageContent>
      <rbz-table
        :columns="columns"
        :table-data="backendServices"
        :sticky-header="true"
        class="rbz-list-table"
        data-qa="backend-services-table"
        :row-buttons="rowButtons"
        :new-button-link="getNewBackendServicePath()"
        :show-filter-row="true"
        @duplicate-row-button-clicked="duplicateBackendService"
        @delete-row-button-clicked="openDeleteBackendServiceModal"
        @download-data="downloadDoc"
      >
      </rbz-table>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="loadBackendServices"
      />
      <rbz-modal-dialog
        :show="showDeleteBackendServiceModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteBackendService"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteBackendServiceModal = false"
        @closeModal="showDeleteBackendServiceModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedBackendService.name }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import _ from 'lodash';
import { defineComponent } from 'vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import { BackendService, DocumentName, GenericObject } from '@/types';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { BACKEND_SERVICES_STICKINESS_MODELS } from '@/types/const';
import PageTemplate from '@/views/PageTemplate.vue';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';
import { useUserStore } from '@/store/UserStore';

export default defineComponent({
  name: 'BackendServicesList',
  setup() {
    const { getEditPath, getNewPath, navigateTo } = useRouting();
    return {
      getEditPath,
      getNewPath,
      navigateTo,
    };
  },
  components: {
    GitHistory,
    PageTemplate,
    RbzModalDialog,
    RbzTable,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('backendService.listPage.name'),
          fieldNames: ['name'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'clickable',
          autoFocus: true,
          linkCallback: (item) => {
            return this.getBackendServicePath(item);
          },
        },
        {
          id: 'hosts',
          title: this.$t('backendService.listPage.backHosts'),
          fieldNames: ['back_hosts'],
          displayFunction: (item: BackendService) => {
            return _.map(item.back_hosts, 'host').join('\n');
          },
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'multi-line white-space-pre-line',
        },
        {
          id: 'transport-protocol',
          title: this.$t('backendService.listPage.transportProtocol'),
          fieldNames: ['transport_mode'],
          displayFunction: (item: BackendService) => {
            const map: GenericObject = {
              default: this.$t('backendService.transportProtocolValue.perRequest'),
              http: this.$t('backendService.transportProtocolValue.http'),
              https: this.$t('backendService.transportProtocolValue.https'),
              port_bridge: this.$t('backendService.transportProtocolValue.portBridge'),
            };
            return map[item.transport_mode];
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-180px',
        },
        {
          id: 'stickiness-model',
          title: this.$t('backendService.listPage.loadBalancingStickinessModel'),
          fieldNames: ['sticky'],
          displayFunction: (item: BackendService) => {
            return BACKEND_SERVICES_STICKINESS_MODELS.find((protocol) => {
              return item.sticky === protocol.value;
            }).name;
          },
          isSortable: true,
          isSearchable: true,
          classes: 'width-170px',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          linkCallback: this.getBackendServicePath,
        },
        {
          id: 'duplicate',
          tooltip: this.$t('common.operations.duplicate.tableTooltip'),
          icon: 'IconDuplicate',
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: this.isDeleteDisabled,
          tooltipCallback: this.deleteTooltip,
        },
      ] as RbzTableRowButton[],
      backendServices: null as BackendService[],
      pageLoadingCounter: null,

      showDeleteBackendServiceModal: false,
      selectedBackendService: null,

      referencedIds: [],
    };
  },

  watch: {
    selectedBranch: {
      handler: async function (val, oldVal) {
        if (
          (this.$route.name as string).includes('Backend Services List') &&
          val &&
          val !== oldVal
        ) {
          this.setPageLoadingStatus(true);
          await this.loadBackendServices();
          await this.loadReferencedIds();
          this.setPageLoadingStatus(false);
        }
      },
      immediate: true,
    },
  },

  computed: {
    selectedBranch(): string {
      if (!this.branchesStore.selectedBranchId) {
        this.branchesStore.setSelectedBranch(this.branchesStore.defaultBranch);
      }
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('backendService.plural'),
        singular: this.$t('backendService.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/backend-services/versions`;
    },
  },

  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    isDeleteDisabled(backendService: BackendService) {
      return (
        this.isBackendServiceProtected(backendService) ||
        this.isBackendServiceReferenced(backendService)
      );
    },

    isBackendServiceProtected(backendService: BackendService): boolean {
      return backendService.id.startsWith('__');
    },

    isBackendServiceReferenced(backendService: BackendService): boolean {
      return this.referencedIds.includes(backendService.id);
    },

    deleteTooltip(backendService: BackendService): string {
      if (this.isBackendServiceProtected(backendService)) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.isBackendServiceReferenced(backendService)) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    getBackendServicePath(backendService: BackendService) {
      return this.getEditPath('backend-services', backendService.id);
    },

    editBackendService(backendService: BackendService) {
      this.navigateTo(this.getBackendServicePath(backendService));
    },

    async duplicateBackendService(backendService: BackendService) {
      this.setPageLoadingStatus(true);
      const getOriginalResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/backend-services/${backendService.id}`,
      });
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      if (!getOriginalResponse?.data) {
        Utils.toast({
          message: failureMessage,
          type: 'is-danger',
        });
        return;
      }
      const originalBackendService = getOriginalResponse.data;
      const backendServiceToAdd = _.cloneDeep(originalBackendService);
      backendServiceToAdd.id = DatasetsUtils.generateUUID2();
      backendServiceToAdd.name = Utils.generateUniqueEntityName(
        backendServiceToAdd.name,
        _.map(this.backendServices, 'name'),
        true
      );
      _.forEach(backendServiceToAdd['back_hosts'], (entry) => {
        delete entry.id;
        entry.host = entry.host.trim();
      });
      if (backendServiceToAdd['mtls_certificate'] === 'off') {
        backendServiceToAdd['mtls_certificate'] = '';
      }
      if (backendServiceToAdd['mtls_trusted_certificate'] === 'off') {
        backendServiceToAdd['mtls_trusted_certificate'] = '';
      }
      const url = `conf/${this.selectedBranch}/backend-services/${backendServiceToAdd.id}`;
      const postNewResponse = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: backendServiceToAdd,
        successMessage,
        failureMessage,
      });
      if (postNewResponse) {
        this.editBackendService(backendServiceToAdd);
      }
      this.setPageLoadingStatus(false);
    },

    async deleteBackendService() {
      this.setPageLoadingStatus(true);
      const backendServiceId = this.selectedBackendService.id;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/backend-services/${backendServiceId}`,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.backendServices = _.filter(this.backendServices, (backendService) => {
          return backendService.id !== backendServiceId;
        });
      }
      this.showDeleteBackendServiceModal = false;
      this.setPageLoadingStatus(false);
    },

    downloadDoc() {
      Utils.downloadFile('backend-services', 'json', this.backendServices);
    },

    openDeleteBackendServiceModal(backendService: BackendService) {
      this.showDeleteBackendServiceModal = true;
      this.selectedBackendService = backendService;
    },

    async loadBackendServices() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/backend-services`;
      const fieldNames = _.flatMap(this.columns, 'fieldNames');
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      this.backendServices = response?.data?.items || [];
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
      });
      const securityPolicies = response?.data?.items || [];
      const referencedIds: string[] = [];
      _.forEach(securityPolicies, (securityPolicy) => {
        _.forEach(securityPolicy.map, (location) => {
          referencedIds.push(location['backend_service']);
        });
      });
      this.referencedIds = _.uniq(referencedIds);
      this.setPageLoadingStatus(false);
    },

    getNewBackendServicePath() {
      return this.getNewPath('backend-services');
    },
  },
});
</script>
